import { Order, Tractor, Location } from "@/types/tms";
import { OrderFormData } from "@/components/orders/orderFormTypes";
import { formLocalToUtc } from "@/lib/timezone";
import type { CustomFieldEntry } from "@/lib/eapi-custom-fields";

/** Ensure a datetime string is full RFC 3339 (with seconds and Z suffix). */
function toRfc3339(dt: string): string {
  if (!dt) return dt;
  // Already has timezone info (Z or +/- offset) — normalise via Date
  if (/Z|[+-]\d{2}:\d{2}$/.test(dt)) {
    return new Date(dt).toISOString();
  }
  // Missing seconds, e.g. "2026-04-06T04:00"
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dt)) {
    console.warn(
      "[eapi-trip-mapper] toRfc3339 received tz-less local string — interpreting as UTC. " +
      "Caller should resolve local→UTC at the stop's timezone first. Value:",
      dt,
    );
    return new Date(dt + ":00Z").toISOString();
  }
  // Has seconds but no timezone, e.g. "2026-04-06T04:00:00"
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(dt)) {
    console.warn(
      "[eapi-trip-mapper] toRfc3339 received tz-less local string — interpreting as UTC. " +
      "Caller should resolve local→UTC at the stop's timezone first. Value:",
      dt,
    );
    return new Date(dt + "Z").toISOString();
  }
  // Fallback — try to parse
  return new Date(dt).toISOString();
}

function normalizeCountryForEapi(country?: string | null): string {
  const normalized = (country || "US").trim().toUpperCase();
  if (normalized === "USA" || normalized === "UNITED STATES" || normalized === "UNITED STATES OF AMERICA") {
    return "US";
  }
  return normalized || "US";
}

interface TrailerTypeInfo {
  kind: string;
  length_ft: number;
}

interface EapiAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

interface EapiLatLng {
  latitude: number;
  longitude: number;
}

interface EapiLocation {
  address: EapiAddress;
  latlng: EapiLatLng | null;
}

interface EapiTimeWindow {
  start_time: string;
  end_time: string;
}

/** Bobtail trips carry no trailer length — EAPI expects an explicit null. */
export interface EapiTrailer {
  kind: string;
  length_ft: number | null;
}

export interface EapiCreateTripBody {
  name: string;
  origin: EapiLocation;
  destination: EapiLocation;
  planned_departure_window: EapiTimeWindow;
  planned_arrival_window: EapiTimeWindow;
  trailer?: EapiTrailer;
  vehicle?: { vehicle_name: string; vin: string };
  custom_fields?: CustomFieldEntry[];
}

export interface EapiPatchTripBody {
  origin?: EapiLocation;
  destination?: EapiLocation;
  planned_departure_window?: EapiTimeWindow;
  planned_arrival_window?: EapiTimeWindow;
  trailer?: EapiTrailer;
}

/**
 * Compare an EAPI createTrip request body against the response payload to
 * detect cases where EAPI deduped/idempotently returned a pre-existing trip
 * whose planned windows or assets differ from what we just requested
 * (e.g. AOP-DETM-01596 L1 returned 06/15 windows for a 06/16 request).
 *
 * Returns a Partial<EapiPatchTripBody> with ONLY the fields that need to be
 * corrected via a follow-up PATCH, or `null` if the response matches the
 * request closely enough.
 */
export function diffCreateResponseForCorrection(
  request: EapiCreateTripBody,
  response: any,
): Partial<EapiPatchTripBody> | null {
  if (!response || typeof response !== "object") return null;
  const delta: Partial<EapiPatchTripBody> = {};

  const eqIso = (a?: string | null, b?: string | null) => {
    if (!a || !b) return a === b;
    const ta = new Date(a).getTime();
    const tb = new Date(b).getTime();
    if (Number.isNaN(ta) || Number.isNaN(tb)) return a === b;
    return ta === tb;
  };

  const respDep = response.planned_departure_window ?? null;
  const reqDep = request.planned_departure_window;
  if (
    reqDep &&
    (!respDep ||
      !eqIso(respDep.start_time, reqDep.start_time) ||
      !eqIso(respDep.end_time, reqDep.end_time))
  ) {
    delta.planned_departure_window = reqDep;
  }

  const respArr = response.planned_arrival_window ?? null;
  const reqArr = request.planned_arrival_window;
  if (
    reqArr &&
    (!respArr ||
      !eqIso(respArr.start_time, reqArr.start_time) ||
      !eqIso(respArr.end_time, reqArr.end_time))
  ) {
    delta.planned_arrival_window = reqArr;
  }

  // Trailer kind/length — only force when both sides provided values that
  // differ; missing response trailer info is not a stale signal.
  if (request.trailer && response.trailer) {
    const norm = (v: unknown) => (v === null || v === undefined ? null : Number(v));
    if (
      response.trailer.kind !== request.trailer.kind ||
      norm(response.trailer.length_ft) !== norm(request.trailer.length_ft)
    ) {
      delta.trailer = request.trailer;
    }
  }

  return Object.keys(delta).length > 0 ? delta : null;
}

function buildAddress(
  street: string,
  city: string,
  state?: string | null,
  postalCode?: string | null,
  country?: string | null
): EapiAddress {
  return {
    line1: street,
    line2: "",
    city,
    state: state || "",
    postal_code: postalCode || "",
    country: normalizeCountryForEapi(country),
  };
}

function findLatLng(
  street: string,
  city: string,
  side: 'origin' | 'destination',
  locations?: Location[] | null,
  locationId?: string | null,
): EapiLatLng | null {
  if (!locations) {
    if (locationId) {
      console.warn('[eapi-trip-mapper] latlng unresolved: locations cache not loaded', {
        side,
        locationId,
        street,
        city,
      });
    }
    return null;
  }
  // Prefer the explicit FK when the order references a saved location —
  // address strings may diverge slightly from the canonical `locations` row.
  let match = locationId
    ? locations.find((l) => l.id === locationId)
    : undefined;
  if (!match) {
    match = locations.find(
      (l) => l.street_address === street && l.city === city,
    );
  }
  if (!match) {
    console.warn('[eapi-trip-mapper] latlng unresolved: no matching location row', {
      side,
      locationId,
      street,
      city,
    });
    return null;
  }
  const lat = side === 'origin'
    ? match.origin_latitude
    : match.destination_latitude;
  const lng = side === 'origin'
    ? match.origin_longitude
    : match.destination_longitude;
  if (lat != null && lng != null) {
    return { latitude: lat, longitude: lng };
  }
  console.warn('[eapi-trip-mapper] latlng unresolved: matched row has no coords', {
    side,
    locationId: match.id,
    street,
    city,
    hasOriginLatitude: match.origin_latitude != null,
    hasDestinationLatitude: match.destination_latitude != null,
  });
  return null;
}

function buildLocation(
  street: string,
  city: string,
  state?: string | null,
  postalCode?: string | null,
  country?: string | null,
  locations?: Location[] | null,
  side: 'origin' | 'destination' = 'origin',
  locationId?: string | null,
): EapiLocation {
  return {
    address: buildAddress(street, city, state, postalCode, country),
    latlng: findLatLng(street, city, side, locations, locationId),
  };
}

function buildVehicle(tractor?: Tractor | null): { vehicle_name: string; vin: string } | undefined {
  if (!tractor) return undefined;
  return { vehicle_name: tractor.name, vin: tractor.vin || "" };
}

function buildTrailer(trailerType?: TrailerTypeInfo | null): EapiTrailer | undefined {
  if (!trailerType) return undefined;
  // Bobtail = tractor only. Length is meaningless (stored as 0 locally);
  // EAPI expects `null`.
  if (trailerType.kind === "bobtail") return { kind: "bobtail", length_ft: null };
  return { kind: trailerType.kind, length_ft: trailerType.length_ft };
}

export function mapOrderToCreateTrip(
  order: Order,
  tractor?: Tractor | null,
  trailerType?: TrailerTypeInfo | null,
  locations?: Location[] | null,
  customFields?: CustomFieldEntry[] | null,
): EapiCreateTripBody {
  return {
    name: order.unique_id,
    origin: buildLocation(
      order.ship_from_street_address,
      order.ship_from_city,
      order.ship_from_province_state,
      order.ship_from_postal_zip_code,
      order.ship_from_country,
      locations,
      'origin',
      order.ship_from_location_id ?? null,
    ),
    destination: buildLocation(
      order.ship_to_street_address,
      order.ship_to_city,
      order.ship_to_province_state,
      order.ship_to_postal_zip_code,
      order.ship_to_country,
      locations,
      'destination',
      order.ship_to_location_id ?? null,
    ),
    planned_departure_window: {
      start_time: toRfc3339(order.ready_time),
      end_time: toRfc3339(order.ready_time_end ?? order.ready_time),
    },
    planned_arrival_window: {
      start_time: toRfc3339(order.requested_delivery_time),
      end_time: toRfc3339(order.requested_delivery_time_end ?? order.requested_delivery_time),
    },
    ...(buildTrailer(trailerType) ? { trailer: buildTrailer(trailerType) } : {}),
    ...(buildVehicle(tractor) ? { vehicle: buildVehicle(tractor) } : {}),
    ...(customFields && customFields.length > 0 ? { custom_fields: customFields } : {}),
  };
}

/**
 * Per-leg variant of `mapOrderToCreateTrip`. Builds an EAPI CreateTrip body
 * for a specific leg of a multi-leg order. The trip name is suffixed with
 * `-L<seq>` so each leg's trip is distinguishable on the EAPI side.
 *
 * Times on the leg row are already UTC tz-aware timestamptz strings, so they
 * pass through `toRfc3339` cleanly without the local-naive warnings.
 */
export interface LegLikeForTrip {
  leg_sequence: number;
  ship_from_street_address: string;
  ship_from_city: string;
  ship_from_province_state: string | null;
  ship_from_postal_zip_code: string | null;
  ship_from_country: string | null;
  ship_from_location_id?: string | null;
  ship_to_street_address: string;
  ship_to_city: string;
  ship_to_province_state: string | null;
  ship_to_postal_zip_code: string | null;
  ship_to_country: string | null;
  ship_to_location_id?: string | null;
  ready_time: string;
  ready_time_end: string | null;
  requested_delivery_time: string;
  requested_delivery_time_end: string | null;
}

/** `<order>-L<n>` for multi-leg orders, plain `<order>` for single-leg. */
export function tripNameForLeg(
  parentUniqueId: string,
  legSequence: number,
  legCount?: number,
): string {
  if (legCount !== undefined && legCount <= 1) return parentUniqueId;
  return `${parentUniqueId}-L${legSequence}`;
}

export function mapLegToCreateTrip(
  leg: LegLikeForTrip,
  parentUniqueId: string,
  tractor?: Tractor | null,
  trailerType?: TrailerTypeInfo | null,
  locations?: Location[] | null,
  customFields?: CustomFieldEntry[] | null,
  /** Total legs on the order. When provided, `-L<n>` is only added for
   *  multi-leg orders (legCount > 1). Omitted keeps the legacy suffix. */
  legCount?: number,
): EapiCreateTripBody {
  return {
    name: tripNameForLeg(parentUniqueId, leg.leg_sequence, legCount),
    origin: buildLocation(
      leg.ship_from_street_address,
      leg.ship_from_city,
      leg.ship_from_province_state,
      leg.ship_from_postal_zip_code,
      leg.ship_from_country,
      locations,
      'origin',
      leg.ship_from_location_id ?? null,
    ),
    destination: buildLocation(
      leg.ship_to_street_address,
      leg.ship_to_city,
      leg.ship_to_province_state,
      leg.ship_to_postal_zip_code,
      leg.ship_to_country,
      locations,
      'destination',
      leg.ship_to_location_id ?? null,
    ),
    planned_departure_window: {
      start_time: toRfc3339(leg.ready_time),
      end_time: toRfc3339(leg.ready_time_end ?? leg.ready_time),
    },
    planned_arrival_window: {
      start_time: toRfc3339(leg.requested_delivery_time),
      end_time: toRfc3339(leg.requested_delivery_time_end ?? leg.requested_delivery_time),
    },
    ...(buildTrailer(trailerType) ? { trailer: buildTrailer(trailerType) } : {}),
    ...(buildVehicle(tractor) ? { vehicle: buildVehicle(tractor) } : {}),
    ...(customFields && customFields.length > 0 ? { custom_fields: customFields } : {}),
  };
}

export function mapOrderToPatchTrip(
  order: Order,
  _tractor?: Tractor | null,
  trailerType?: TrailerTypeInfo | null,
  locations?: Location[] | null
): EapiPatchTripBody {
  const body: EapiPatchTripBody = {
    origin: buildLocation(
      order.ship_from_street_address,
      order.ship_from_city,
      order.ship_from_province_state,
      order.ship_from_postal_zip_code,
      order.ship_from_country,
      locations,
      'origin',
      order.ship_from_location_id ?? null,
    ),
    destination: buildLocation(
      order.ship_to_street_address,
      order.ship_to_city,
      order.ship_to_province_state,
      order.ship_to_postal_zip_code,
      order.ship_to_country,
      locations,
      'destination',
      order.ship_to_location_id ?? null,
    ),
    planned_departure_window: {
      start_time: toRfc3339(order.ready_time),
      end_time: toRfc3339(order.ready_time_end ?? order.ready_time),
    },
    planned_arrival_window: {
      start_time: toRfc3339(order.requested_delivery_time),
      end_time: toRfc3339(order.requested_delivery_time_end ?? order.requested_delivery_time),
    },
  };

  const trailer = buildTrailer(trailerType);
  if (trailer) body.trailer = trailer;

  return body;
}

/**
 * Build a delta payload containing only the EAPI fields that actually changed
 * between the original order and the updated form data.
 * Returns `null` if nothing changed (no EAPI PATCH needed).
 *
 * Note: vehicle changes are NOT included — they require a trip pivot
 * (cancel + recreate), which is handled separately.
 */
/** Convert an order-form time (Central wall-clock, see docs/TIMEZONES.md)
 *  into a UTC ISO string. Values that already carry a `Z`/offset are passed
 *  through for `toRfc3339` to normalise. */
function resolveFormTimeToUtc(value: string | null | undefined): string {
  if (!value) return value ?? "";
  if (/Z|[+-]\d{2}:\d{2}$/.test(value)) return value;
  return formLocalToUtc(value) ?? value;
}

export function buildEapiDelta(
  original: Order,
  updated: OrderFormData,
  _tractor?: Tractor | null,
  trailerType?: TrailerTypeInfo | null,
  locations?: Location[] | null
): Partial<EapiPatchTripBody> | null {
  const delta: Partial<EapiPatchTripBody> = {};

  // Compare origin fields
  const originChanged =
    updated.ship_from_street_address !== original.ship_from_street_address ||
    updated.ship_from_city !== original.ship_from_city ||
    (updated.ship_from_province_state || "") !== (original.ship_from_province_state || "") ||
    (updated.ship_from_postal_zip_code || "") !== (original.ship_from_postal_zip_code || "") ||
    (updated.ship_from_country || "") !== (original.ship_from_country || "");

  if (originChanged) {
    delta.origin = buildLocation(
      updated.ship_from_street_address,
      updated.ship_from_city,
      updated.ship_from_province_state,
      updated.ship_from_postal_zip_code,
      updated.ship_from_country,
      locations,
      'origin',
      (updated as { ship_from_location_id?: string | null }).ship_from_location_id
        ?? original.ship_from_location_id
        ?? null,
    );
  }

  // Compare destination fields
  const destChanged =
    updated.ship_to_street_address !== original.ship_to_street_address ||
    updated.ship_to_city !== original.ship_to_city ||
    (updated.ship_to_province_state || "") !== (original.ship_to_province_state || "") ||
    (updated.ship_to_postal_zip_code || "") !== (original.ship_to_postal_zip_code || "") ||
    (updated.ship_to_country || "") !== (original.ship_to_country || "");

  if (destChanged) {
    delta.destination = buildLocation(
      updated.ship_to_street_address,
      updated.ship_to_city,
      updated.ship_to_province_state,
      updated.ship_to_postal_zip_code,
      updated.ship_to_country,
      locations,
      'destination',
      (updated as { ship_to_location_id?: string | null }).ship_to_location_id
        ?? original.ship_to_location_id
        ?? null,
    );
  }

  // Compare departure time window
  const originalReadyNorm = original.ready_time ? new Date(original.ready_time).getTime() : 0;
  const updatedReadyUtc = resolveFormTimeToUtc(updated.ready_time);
  const updatedReadyNorm = updatedReadyUtc ? new Date(updatedReadyUtc).getTime() : 0;
  const originalReadyEndNorm = original.ready_time_end ? new Date(original.ready_time_end).getTime() : originalReadyNorm;
  const updatedReadyEndUtc = resolveFormTimeToUtc(updated.ready_time_end);
  const updatedReadyEndNorm = updatedReadyEndUtc ? new Date(updatedReadyEndUtc).getTime() : updatedReadyNorm;
  if (
    (originalReadyNorm !== updatedReadyNorm || originalReadyEndNorm !== updatedReadyEndNorm) &&
    updatedReadyUtc
  ) {
    delta.planned_departure_window = {
      start_time: toRfc3339(updatedReadyUtc),
      end_time: toRfc3339(updatedReadyEndUtc || updatedReadyUtc),
    };
  }

  // Compare arrival time window
  const originalDeliveryNorm = original.requested_delivery_time ? new Date(original.requested_delivery_time).getTime() : 0;
  const updatedDeliveryUtc = resolveFormTimeToUtc(updated.requested_delivery_time);
  const updatedDeliveryNorm = updatedDeliveryUtc ? new Date(updatedDeliveryUtc).getTime() : 0;
  const originalDeliveryEndNorm = original.requested_delivery_time_end
    ? new Date(original.requested_delivery_time_end).getTime()
    : originalDeliveryNorm;
  const updatedDeliveryEndUtc = resolveFormTimeToUtc(updated.requested_delivery_time_end);
  const updatedDeliveryEndNorm = updatedDeliveryEndUtc
    ? new Date(updatedDeliveryEndUtc).getTime()
    : updatedDeliveryNorm;
  if (
    (originalDeliveryNorm !== updatedDeliveryNorm || originalDeliveryEndNorm !== updatedDeliveryEndNorm) &&
    updatedDeliveryUtc
  ) {
    delta.planned_arrival_window = {
      start_time: toRfc3339(updatedDeliveryUtc),
      end_time: toRfc3339(updatedDeliveryEndUtc || updatedDeliveryUtc),
    };
  }

  // Compare trailer type
  if ((updated.trailer_type_id || "") !== (original.trailer_type_id || "")) {
    const trailer = buildTrailer(trailerType);
    if (trailer) delta.trailer = trailer;
  }

  // Note: vehicle/tractor changes are NOT included in the delta.
  // They require a trip pivot (cancel old + create new), handled separately.

  return Object.keys(delta).length > 0 ? delta : null;
}

/**
 * Hard guard: throws if an outgoing EAPI trip body is missing origin/destination
 * latlng. Call immediately before `createTrip`/`patchTrip` so the upstream EAPI
 * call never goes out with a coord-less stop (which silently breaks ETA /
 * routing). Errors carry `code = 'EAPI_MISSING_LATLNG'` so callers / toasts
 * can branch on them.
 */
export class EapiMissingLatLngError extends Error {
  code = "EAPI_MISSING_LATLNG" as const;
  sides: Array<"origin" | "destination">;
  context: Record<string, unknown>;
  constructor(sides: Array<"origin" | "destination">, context: Record<string, unknown>) {
    super(
      `EAPI request blocked: missing destination coordinates (${sides.join(" & ")}). ` +
      `Open the order and re-pick the destination (or backfill the location FK), then retry.`,
    );
    this.name = "EapiMissingLatLngError";
    this.sides = sides;
    this.context = context;
  }
}

export function assertTripBodyHasLatLng(
  body: {
    origin?: { latlng?: EapiLatLng | null } | null;
    destination?: { latlng?: EapiLatLng | null } | null;
  },
  context: Record<string, unknown> = {},
): void {
  const missing: Array<"origin" | "destination"> = [];
  // Origin latlng is optional — EAPI can resolve pickup from the address alone.
  // Destination latlng is required for accurate ETA/geofence on drop.
  if (body.destination && !body.destination.latlng) missing.push("destination");
  if (missing.length > 0) {
    console.error("[eapi-trip-mapper] blocking EAPI call — missing latlng", {
      missing,
      ...context,
    });
    throw new EapiMissingLatLngError(missing, context);
  }
}
